"use client";

import { cn } from "@universe/ui/lib/utils";
import { format } from "date-fns";
import { useTranslations } from "next-intl";
import React from "react";

import DetailRow from "@/components/detail-row";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import { NoTranslate } from "@/components/no-translate";
import { LEDGER_ENTRY_DATE_TIME_LONG_FORMAT } from "@/constants/dateTime";
import useCurrencyFormatter from "@/hooks/useCurrencyFormatter";

import TransactionStatusBadge, {
  TransactionStatusBadgeType,
} from "../transaction-status-badge";

import LedgerEntryBaseFields from "./ledger-entry-base-fields";
import { MissingResolutionAlert } from "./missing-resolution-alert";

interface FxTicketsDetailsProps {
  entry: LedgerEntryResolvedFxTicket;
}

const ExchangeRate = ({ entry }: { entry: LedgerEntryResolvedFxTicket }) => {
  const { formatCurrency } = useCurrencyFormatter();

  const counterAmount = entry.resolvedData.counterAmount ?? 0;
  const baseAmount = entry.resolvedData.baseAmount ?? 1;
  const rate = counterAmount / baseAmount;
  return (
    <NoTranslate as="div">
      <span>1 {entry.resolvedData.baseCurrency ?? ""} = </span>
      <span>
        <span>
          {formatCurrency(
            Math.abs(rate),
            entry.resolvedData.counterCurrency ?? "",
            true
          )}
        </span>{" "}
        <span>{entry.resolvedData.counterCurrency ?? ""}</span>
      </span>
    </NoTranslate>
  );
};

const InverseRate = ({ entry }: { entry: LedgerEntryResolvedFxTicket }) => {
  const { formatCurrency } = useCurrencyFormatter();
  const counterAmount = entry.resolvedData.counterAmount ?? 0;
  const baseAmount = entry.resolvedData.baseAmount ?? 1;
  const rate = counterAmount / baseAmount;
  return (
    <NoTranslate as="div">
      <span>1 {entry.resolvedData.counterCurrency ?? ""} = </span>
      <span>
        <span>
          {formatCurrency(
            Math.abs(1 / rate),
            entry.resolvedData.baseCurrency ?? "",
            true
          )}
        </span>{" "}
        <span>{entry.resolvedData.baseCurrency ?? ""}</span>
      </span>
    </NoTranslate>
  );
};

export default function FxTicketsDetails({ entry }: FxTicketsDetailsProps) {
  const tLedgerFields = useTranslations("ledger.fields");

  // BFF resolution failed — the rate/leg arithmetic below would all throw
  // on null. Render base fields and a support prompt instead.
  if (!entry.resolvedData) {
    return (
      <div className="space-y-0">
        <LedgerEntryBaseFields entry={entry} />
        <MissingResolutionAlert />
      </div>
    );
  }

  // Determine which currency was purchased vs sold based on credit/debit
  // If entry.credit > 0: this entry's product was purchased
  // If entry.debit > 0: this entry's product was sold
  const entryProductWasPurchased = entry.credit > 0;
  const entryProductIsBase = entry.product === entry.resolvedData.baseCurrency;

  // Determine purchased amount and currency
  let purchasedAmount: number;
  let purchasedCurrency: string;
  let soldAmount: number;
  let soldCurrency: string;

  if (entryProductWasPurchased) {
    // This entry's product was purchased
    if (entryProductIsBase) {
      // Base currency was purchased
      purchasedAmount = Math.abs(entry.resolvedData.baseAmount ?? 0);
      purchasedCurrency = entry.resolvedData.baseCurrency ?? "";
      soldAmount = Math.abs(entry.resolvedData.counterAmount ?? 0);
      soldCurrency = entry.resolvedData.counterCurrency ?? "";
    } else {
      // Counter currency was purchased
      purchasedAmount = Math.abs(entry.resolvedData.counterAmount ?? 0);
      purchasedCurrency = entry.resolvedData.counterCurrency ?? "";
      soldAmount = Math.abs(entry.resolvedData.baseAmount ?? 0);
      soldCurrency = entry.resolvedData.baseCurrency ?? "";
    }
  } else {
    // This entry's product was sold
    if (entryProductIsBase) {
      // Base currency was sold
      soldAmount = Math.abs(entry.resolvedData.baseAmount ?? 0);
      soldCurrency = entry.resolvedData.baseCurrency ?? "";
      purchasedAmount = Math.abs(entry.resolvedData.counterAmount ?? 0);
      purchasedCurrency = entry.resolvedData.counterCurrency ?? "";
    } else {
      // Counter currency was sold
      soldAmount = Math.abs(entry.resolvedData.counterAmount ?? 0);
      soldCurrency = entry.resolvedData.counterCurrency ?? "";
      purchasedAmount = Math.abs(entry.resolvedData.baseAmount ?? 0);
      purchasedCurrency = entry.resolvedData.baseCurrency ?? "";
    }
  }

  return (
    <div className="space-y-0">
      <div
        className={cn(
          "flex flex-col gap-0",
          !entryProductWasPurchased && "flex-col-reverse"
        )}
      >
        <DetailRow
          label={tLedgerFields("amountPurchased")}
          value={
            <FormattedCurrencyAmount
              balance={purchasedAmount}
              product={purchasedCurrency}
              forcePositive
              className="text-base lg:text-lg"
            />
          }
          translateValue={false}
        />
        <DetailRow
          label={tLedgerFields("amountSold")}
          value={
            <FormattedCurrencyAmount
              balance={soldAmount}
              product={soldCurrency}
              forceNegative
              className="text-base lg:text-lg"
            />
          }
          translateValue={false}
        />
      </div>

      <DetailRow
        label={tLedgerFields("status")}
        value={
          <TransactionStatusBadge
            size="xs"
            status={
              (entry.resolvedData.settled
                ? "RECONCILED"
                : "PROCESSING") as TransactionStatusBadgeType
            }
            showReconciled
          />
        }
      />

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("rate")}
        value={
          <div className="flex flex-col flex-gap-1">
            <ExchangeRate entry={entry} />
            <InverseRate entry={entry} />
          </div>
        }
      />

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("tradeCompletedOn")}
        value={format(
          new Date(entry.resolvedData.meta?.createTime || ""),
          LEDGER_ENTRY_DATE_TIME_LONG_FORMAT
        )}
      />

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("referenceId")}
        value={entry.resolvedData.reference}
        translateValue={false}
      />
    </div>
  );
}
